import { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import OperatorUsers from "./Components/OperatorUsers";
import { toast } from "react-toastify";
import SchoolsList from "./Components/SchoolsList";
import OperatorRoles from "./Components/OperatorRoles";
import { FaSave, FaTimes } from "react-icons/fa";
import api from "../../api/axiosInstance";

const ManageOperators = () => {
  const pId = useParams();
  const [operator, setOperator] = useState({});
  const [errors, setErrors] = useState({});
  const [rolesUpdateKey, setRolesUpdateKey] = useState(0);

  // Refs for focusing on error fields
  const operatorNameRef = useRef(null);
  const operatorCodeRef = useRef(null);
  const getOperatorInfo = (operatorId) => {
    var config = {
      method: "Get",
      url:
        process.env.REACT_APP_API_URL + "Operators/GetOperator/" + operatorId,
      headers: {
        "Access-Control-Allow-Origin": process.env.REACT_APP_Host,
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
      },
    };

    api(config)
      .then(function (response) {
        if (response.data.statusCode === 200) {
          setOperator(response.data.content);
          setErrors({});
        }
      })
      .catch(function (error) {
        if (error?.response?.data?.errors) {
          const validationErrors = error.response.data.errors;
          setErrors(validationErrors);
          focusFirstErrorField(validationErrors);
          toast.warning("Please review and correct the required fields");
        }
      });
  };

  const focusFirstErrorField = (validationErrors) => {
    // Use setTimeout to ensure DOM is updated with error messages before focusing
    setTimeout(() => {
      // Focus on the first field with an error in order: OperatorName, OperatorCode
      if (validationErrors.OperatorName && operatorNameRef.current) {
        operatorNameRef.current.focus();
      } else if (validationErrors.OperatorCode && operatorCodeRef.current) {
        operatorCodeRef.current.focus();
      }
    }, 100);
  };

  // Map between PascalCase (API errors) and camelCase (state)
  const fieldNameMap = {
    OperatorName: "operatorName",
    OperatorCode: "operatorCode",
  };

  const handleInputChange = (apiFieldName, value) => {
    const stateFieldName = fieldNameMap[apiFieldName] || apiFieldName;

    setOperator((prevState) => ({
      ...prevState,
      [stateFieldName]: value,
    }));

    // Clear error for this field when user starts typing (using API field name for errors)
    if (errors[apiFieldName]) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[apiFieldName];
        return newErrors;
      });
    }
  };

  const updateOperator = () => {
    // Validate required fields before submission
    const validationErrors = {};
    let hasErrors = false;

    if (!operator.operatorName || operator.operatorName.trim() === "") {
      validationErrors.OperatorName = ["The OperatorName field is required."];
      hasErrors = true;
    }

    if (!operator.operatorCode || operator.operatorCode.trim() === "") {
      validationErrors.OperatorCode = ["The OperatorCode field is required."];
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(validationErrors);
      focusFirstErrorField(validationErrors);
      toast.warning("Please review and correct the required fields");
      return;
    }

    // Clear errors if validation passes
    setErrors({});

    var config = {
      method: "Post",
      url: process.env.REACT_APP_API_URL + "Operators/EditOperator/",
      headers: {
        "Access-Control-Allow-Origin": process.env.REACT_APP_Host,
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
      },
      data: operator,
    };

    api(config)
      .then(function (response) {
        if (response.data.statusCode === 200) {
          toast.success("Operator Updated Successfully");
          setOperator(response.data.content);
          setErrors({});
        }
      })
      .catch(function (error) {
        if (error?.response?.data?.errors) {
          const validationErrors = error.response.data.errors;
          setErrors(validationErrors);
          focusFirstErrorField(validationErrors);
          toast.warning("Please review and correct the required fields");
        } else {
          toast.error(
            error?.response?.data?.message || "Failed to update operator",
          );
        }
      });
  };

  useEffect(() => {
    if (pId.OperatorId != 0) {
      getOperatorInfo(pId.OperatorId);
      setOperator((prevState) => ({
        ...prevState,
        operatorId: pId.OperatorId,
      }));
    }
  }, []);
  return (
    <>
      <div className="container-fluid mt-30">
        <div className="row">
          <div className="col-12 mb-3">
            <div className="card">
              <div className="card-header">
                <h3>Operator - {operator.operatorName}</h3>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-4 col-lg-6 col-xl-4">
            <div className="card mb-3">
              <div className="card-header">
                <h3 className="card-title">Manage Operator</h3>
                <div className="self-content-center d-flex gap-2">
                  <Link
                    to={"/Settings/Operators"}
                    className="btn btn-sm btn-secondary cts-btn-secondary d-flex align-items-center gap-2"
                  >
                    <FaTimes size={14} />
                    Cancel
                  </Link>
                  <button
                    className="btn btn-sm btn-success cts-btn-success d-flex align-items-center gap-2"
                    onClick={() => updateOperator()}
                  >
                    <FaSave size={14} />
                    Save
                  </button>
                </div>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-12 mb-3">
                    <b>
                      Name <span className="text-danger">*</span>
                    </b>
                    <input
                      ref={operatorNameRef}
                      className={`form-control form-control-sm ${errors.OperatorName ? "is-invalid" : ""}`}
                      value={operator.operatorName || ""}
                      onChange={(e) =>
                        handleInputChange("OperatorName", e.target.value)
                      }
                    />
                    {errors.OperatorName && (
                      <div
                        className="text-warning"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {errors.OperatorName[0]}
                      </div>
                    )}
                  </div>
                  <div className="col-12 mb-3">
                    <b>
                      Code <span className="text-danger">*</span>
                    </b>
                    <input
                      ref={operatorCodeRef}
                      className={`form-control form-control-sm ${errors.OperatorCode ? "is-invalid" : ""}`}
                      value={operator.operatorCode || ""}
                      onChange={(e) =>
                        handleInputChange("OperatorCode", e.target.value)
                      }
                    />
                    {errors.OperatorCode && (
                      <div
                        className="text-warning"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {errors.OperatorCode[0]}
                      </div>
                    )}
                  </div>
                  <div className="col-12">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="flexSwitchCheckChecked"
                        checked={operator.isLocked == false ? true : false}
                        onChange={(e) => {
                          setOperator((prevState) => ({
                            ...prevState,
                            isActive: e.target.checked,
                          }));
                          setOperator((prevState) => ({
                            ...prevState,
                            isLocked: e.target.checked == true ? false : true,
                          }));
                        }}
                      />
                      <label
                        className="form-check-label"
                        for="flexSwitchCheckChecked"
                      >
                        Is Active
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-header">
                <h5 className="card-title text-white">Operator Schools</h5>
              </div>
              <div className="card-body">
                <SchoolsList operatorId={pId.OperatorId} />
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-6 col-xl-8">
            <OperatorUsers
              operatorId={pId.OperatorId}
              rolesUpdateKey={rolesUpdateKey}
            />
            <br></br>
            <OperatorRoles
              operatorId={pId.OperatorId}
              onRolesUpdated={() => setRolesUpdateKey((prev) => prev + 1)}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageOperators;
